
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router';
import { css } from 'emotion';

import Modal, { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import { useToasts } from '@vromo/ui/components/Toasts';
import { IntegrationsConfig, Platform } from '@vromo/models';
import OverlayablePanel from '@vromo/ui/components/Overlay';
import Title from '@vromo/ui/components/Title';
import Button from '@vromo/ui/components/Button';
import { useIntegrationsActions, useIntegrationsNav, useIntegrationsPage } from '../hooks';
import { firstLetterCap, useModals } from '@vromo/ui';
import { TrayConfigIframe } from '../components';




/** modal to created / add a new integration */
export const IntegrationConfigure = observer(() => {

    const {
        finishIntegrationConfig, createIntegrationConfig, cancelIntegrationConfig, fetchIntegrations
    } = useIntegrationsActions()
    const { isLoading, fetched, fetchedIntegrations, allPosibleIntegrations } = useIntegrationsPage()
    const { modalOpen, closeModal, goToWindow } = useIntegrationsNav({ modalOpened: true })
    const showToast = useToasts();
    const modals = useModals();
    const { platform } = useParams<{ platform: Platform }>()
    const instance = fetchedIntegrations.get(platform)
    const [configCompleted, setConfigCompleted] = useState(false)
    const { features: { iframe = false } = {} } = IntegrationsConfig[platform] ?? {}



    const actionText = useMemo(() => ({
        discard: instance?.completed ?
            "discard changes" :
            "discard creation",
        save: "done"
    }), [instance])

    // return to integrations home if integration platform is not available
    useEffect(() => {
        if (!allPosibleIntegrations.includes(platform)) {
            showToast(`${platform} integration is not configurable`, 'warning')
            goToWindow("list")
        }
    }, [platform, goToWindow])

    // fetch or create integration if required. Fetch config wizard url
    useEffect(() => {
        if (
            /* (!instance && fetched && !isLoading) ||
            (fetched && instance?.ext_ref && iframe && !instance?.features?.wizardUrl && !isLoading) */
            fetched && iframe && !instance?.features?.wizardUrl && !isLoading
        ) {
            (async () => {
                await createIntegrationConfig(platform)
                // the tray "finish" workflow url is needed, not built on integration creation, but when it is fetched
                !instance?.features && fetchIntegrations()
            })()
        }
    }, [instance, fetched, isLoading, iframe])



    const onCancel = async ({ skip = false } = {}) => {
        if (!skip)
            await modals.confirm(
                "cancel changes",
                `are you sure you want to cancel? Your changes will not be saved`
            )
        setConfigCompleted(true)
        closeModal()
        iframe && cancelIntegrationConfig(platform)
        fetchIntegrations()
    }




    return (
        <Modal isOpen={modalOpen} onRequestClose={onCancel} showCloseButton={!isLoading} >
            <OverlayablePanel active={isLoading} loadingText='Loading'>
                <div>
                    <ModalHeader css={firstLetterCap}>
                        <Title>{platform} integration configuration</Title>
                    </ModalHeader>
                    {
                        fetched && instance &&
                        <ModalContent >
                            <TrayConfigIframe
                                onComplete={() => setConfigCompleted(true)}
                                onCancel={() => onCancel({ skip: true })}
                            />
                        </ModalContent>
                    }
                    {
                        fetched && instance &&
                        iframe && configCompleted &&
                        <ModalActions >
                            {
                                !configCompleted &&
                                <Button onClick={() => onCancel()}>
                                    {actionText.discard}
                                </Button>
                            }
                            {
                                configCompleted &&
                                <Button
                                    primary
                                    onClick={async () => {
                                        finishIntegrationConfig(platform)
                                        await closeModal()
                                        cancelIntegrationConfig(platform)
                                    }}
                                >
                                    {actionText.save}
                                </Button>
                            }
                        </ModalActions>
                    }
                </div>
            </OverlayablePanel>
        </Modal >
    );
});




